import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchRinascenteInvoices, fetchRinascenteCount, fetchRinascenteSuggestions, checkNewRinascente } from "../../api/client";
import { StatusIcon } from "../../lib/status";
import { LockChip } from "../../lib/LockChip";
import type { RinascenteInvoiceListItem, RinascenteSuggestion, RinascenteSortKey } from "../../api/types";
import { markRinascenteSeen } from "../../hooks/useRinascentePendingCount";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];
const REFRESH_INTERVAL = 30_000;

const STATUS_OPTIONS = [
  { value: "all", label: "Tutte" },
  { value: "pending_review", label: "Da rivedere" },
  { value: "approved", label: "Approvate" },
  { value: "rejected", label: "Rifiutate" },
  { value: "error", label: "Errore" },
  { value: "not_invoice", label: "Non fattura" },
];

const TIPO_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "fattura", label: "Fatture" },
  { value: "credit_memo", label: "Credit memo" },
];

// Resa stato unificata → lib/status
function getStatusIcon(s: string) {
  return <StatusIcon status={s} />;
}

// Completezza: verde ≥80% / arancio ≥60% / rosso <60%
function completenessColor(v: number) {
  if (v >= 0.80) return "var(--indaco-success)";
  if (v >= 0.60) return "var(--indaco-warning)";
  return "var(--indaco-danger)";
}

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    const dayMonth = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${dayMonth} ${time}`;
  } catch { return iso; }
}

function formatDateFull(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtDateOnly(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}

export function RinascenteList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Lettura filtri da URL ──
  const status = searchParams.get("status") || "pending_review";
  const tipo = searchParams.get("tipo") || "all";
  const fQ = searchParams.get("q") || "";
  const fNumero = searchParams.get("numero") || "";
  const fFornitore = searchParams.get("fornitore") || "";
  const fTotale = searchParams.get("totale") || "";
  const fDataDa = searchParams.get("data_da") || "";
  const fDataA = searchParams.get("data_a") || "";
  const sort = searchParams.get("sort") || "date_desc";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("size") || String(DEFAULT_PAGE_SIZE), 10);

  const [data, setData] = useState<{ items: RinascenteInvoiceListItem[]; total: number } | null>(null);
  const [counts, setCounts] = useState<{ pending: number; approvedToday: number; total: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState<string | null>(null);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ── Search bar state ──
  const [searchText, setSearchText] = useState(fQ);
  const [suggestions, setSuggestions] = useState<RinascenteSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestIdx, setSuggestIdx] = useState(-1);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const suggestTimer = useRef<ReturnType<typeof setTimeout>>();
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [lastVisitedId, setLastVisitedId] = useState<string | null>(() => localStorage.getItem("lastVisitedRinascenteId"));

  useEffect(() => { setSearchText(fQ); }, [fQ]);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!fQ && !searchText) return;
    function handleClick(e: MouseEvent) {
      if (searchContainerRef.current?.contains(e.target as Node)) return;
      setSuggestions([]);
    }
    const t = setTimeout(() => document.addEventListener("mousedown", handleClick), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handleClick); };
  }, [fQ, searchText]);

  function handleSearchInput(val: string) {
    setSearchText(val);
    setSuggestIdx(-1);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (val.length < 2) { setSuggestions([]); return; }
    setSuggestLoading(true);
    suggestTimer.current = setTimeout(() => {
      fetchRinascenteSuggestions(val)
        .then(r => setSuggestions(r))
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestLoading(false));
    }, 300);
  }

  function applySearch(q: string) {
    setSuggestions([]);
    setFilter({ q: q.trim() || null });
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { setSuggestions([]); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSuggestIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setSuggestIdx(i => Math.max(i - 1, -1)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestIdx >= 0 && suggestions[suggestIdx]) {
        navigate(`/rinascente/${suggestions[suggestIdx].id}`);
        setSuggestions([]);
      } else {
        applySearch(searchText);
      }
    }
  }

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!openFilter) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest("[data-filter-popover]") || target.closest("[data-filter-toggle]")) return;
      setOpenFilter(null);
    }
    const timer = setTimeout(() => document.addEventListener("click", handleClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener("click", handleClick); };
  }, [openFilter]);

  function setFilter(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "" || v === undefined) next.delete(k);
        else next.set(k, v);
      }
      if (!("page" in updates)) next.delete("page");
      return next;
    }, { replace: true });
  }

  function setTextFilter(key: string, value: string) {
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => setFilter({ [key]: value || null }), 300);
  }

  const loadCounts = useCallback(() => {
    fetchRinascenteCount()
      .then((c) => {
        setCounts({
          pending: c.pending_review,
          approvedToday: c.approved_today,
          total: c.total,
        });
      })
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setIsLoading(true);
    fetchRinascenteInvoices({
      status: (status === "all" || !status) ? undefined : status,
      tipo: (tipo === "all" || !tipo) ? undefined : tipo,
      q: fQ || undefined,
      sort: sort as RinascenteSortKey,
      page,
      page_size: pageSize,
    })
      .then((d) => { setData({ items: d.items, total: d.total }); setError(null); setLastRefresh(Date.now()); })
      .catch((e) => setError(e instanceof Error ? e.message : "Errore caricamento"))
      .finally(() => setIsLoading(false));
  }, [status, tipo, fQ, sort, page, pageSize]);

  // "Forza aggiornamento": Rinascente non ha una mailbox — i PDF arrivano su
  // disco. Forza subito una scansione della cartella sorgente, poi ricarica
  // lista + contatori.
  const handleCheckNew = useCallback(async () => {
    setChecking(true);
    setCheckMsg(null);
    try {
      const r = await checkNewRinascente();
      setCheckMsg(r.error ? `Errore: ${r.error}` : r.new > 0 ? `${r.new} nuove fatture` : "Nessun nuovo file");
      load();
      loadCounts();
    } catch {
      setCheckMsg("Errore nel controllo");
    } finally {
      setChecking(false);
      setTimeout(() => setCheckMsg(null), 5000);
    }
  }, [load, loadCounts]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  // Azzera il badge "nuovi" della sidebar al mount della lista.
  useEffect(() => { markRinascenteSeen(); }, []);
  useEffect(() => {
    const id = setInterval(() => { load(); loadCounts(); }, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [load, loadCounts]);
  useEffect(() => {
    const id = setInterval(() => setSecondsAgo(Math.floor((Date.now() - lastRefresh) / 1000)), 1000);
    return () => clearInterval(id);
  }, [lastRefresh]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;
  const hasAnyColumnFilter = !!(fQ || fNumero || fFornitore || fTotale || fDataDa || fDataA);

  // ── Column header con sort + filter popover (replica DDT) ──
  function ColHeader({ label, sortKey, filterKey, filterType, filterValue, options, style }:
    { label: string; sortKey: string; filterKey?: string; filterType?: "text" | "select";
      filterValue?: string; options?: { value: string; label: string }[]; style?: React.CSSProperties }) {
    const isAsc = sort === `${sortKey}_asc`;
    const isDesc = sort === `${sortKey}_desc`;
    const isSortActive = isAsc || isDesc;
    const nextSort = isAsc ? `${sortKey}_desc` : isDesc ? null : `${sortKey}_asc`;
    const isFiltered = filterKey ? Boolean(filterValue) : false;
    const isOpen = openFilter === sortKey;

    return (
      <th style={{ position: "relative", ...style }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2, userSelect: "none" }}>
          <span style={{ cursor: "pointer", flex: 1 }} onClick={() => setFilter({ sort: nextSort })}>
            {label}
          </span>
          <span className={`material-symbols-outlined${isSortActive ? "" : " th-icon-hover"}`}
            onClick={() => setFilter({ sort: nextSort })}
            style={{ fontSize: 14, cursor: "pointer", padding: 2,
                     color: "var(--indaco-brand-primary)", opacity: isSortActive ? 1 : 0.6 }}>
            {isAsc ? "arrow_upward" : isDesc ? "arrow_downward" : "swap_vert"}
          </span>
          {filterKey && (
            <span className={`material-symbols-outlined${isFiltered ? "" : " th-icon-hover"}`}
              data-filter-toggle onClick={() => setOpenFilter(isOpen ? null : sortKey)}
              style={{ fontSize: 14, cursor: "pointer", padding: 2,
                       color: "var(--indaco-brand-primary)", opacity: isFiltered ? 1 : 0.6,
                       background: isFiltered ? "rgba(var(--indaco-brand-primary-rgb),0.1)" : "transparent",
                       borderRadius: 4 }}>
              filter_alt
            </span>
          )}
        </div>
        {isOpen && filterKey && (
          <div data-filter-popover
            style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, minWidth: 180,
                     background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)",
                     borderRadius: "var(--border-radius-md)", boxShadow: "var(--shadow-lg)",
                     padding: "var(--space-2) var(--space-3)", marginTop: 4 }}>
            {filterType === "select" && options && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {options.map((o) => (
                  <button key={o.value}
                    className={`btn btn-sm ${filterValue === o.value || (!filterValue && !o.value) ? "btn-primary" : "btn-ghost"}`}
                    style={{ justifyContent: "flex-start", fontSize: "var(--font-size-xs)", padding: "var(--space-1) var(--space-2)" }}
                    onClick={() => { setFilter({ [filterKey]: o.value || null }); setOpenFilter(null); }}>
                    {o.label}
                  </button>
                ))}
              </div>
            )}
            {filterType === "text" && (
              <input autoFocus type="text" className="form-input"
                style={{ fontSize: "var(--font-size-xs)", padding: "var(--space-1) var(--space-2)", width: "100%" }}
                placeholder={`Filtra ${label.toLowerCase()}...`}
                defaultValue={filterValue}
                onChange={(e) => setTextFilter(filterKey, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setOpenFilter(null); }} />
            )}
            {filterValue && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: "var(--font-size-xs)", width: "100%", color: "var(--indaco-danger)" }}
                onClick={() => { setFilter({ [filterKey]: null }); setOpenFilter(null); }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13, marginRight: 4 }}>close</span>Rimuovi filtro
              </button>
            )}
          </div>
        )}
      </th>
    );
  }

  // Colonna Data emissione: sort + popover con due input (da / a)
  function DateRangeHeader({ style }: { style?: React.CSSProperties }) {
    const isAsc = sort === "date_asc";
    const isDesc = sort === "date_desc";
    const isSortActive = isAsc || isDesc;
    const nextSort = isAsc ? "date_desc" : isDesc ? null : "date_asc";
    const isFiltered = !!(fDataDa || fDataA);
    const isOpen = openFilter === "data_emissione";

    return (
      <th style={{ position: "relative", ...style }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2, userSelect: "none" }}>
          <span style={{ cursor: "pointer", flex: 1 }} onClick={() => setFilter({ sort: nextSort })}>Data emiss.</span>
          <span className={`material-symbols-outlined${isSortActive ? "" : " th-icon-hover"}`}
            onClick={() => setFilter({ sort: nextSort })}
            style={{ fontSize: 14, cursor: "pointer", padding: 2,
                     color: "var(--indaco-brand-primary)", opacity: isSortActive ? 1 : 0.6 }}>
            {isAsc ? "arrow_upward" : isDesc ? "arrow_downward" : "swap_vert"}
          </span>
          <span className={`material-symbols-outlined${isFiltered ? "" : " th-icon-hover"}`}
            data-filter-toggle onClick={() => setOpenFilter(isOpen ? null : "data_emissione")}
            style={{ fontSize: 14, cursor: "pointer", padding: 2,
                     color: "var(--indaco-brand-primary)", opacity: isFiltered ? 1 : 0.6,
                     background: isFiltered ? "rgba(var(--indaco-brand-primary-rgb),0.1)" : "transparent",
                     borderRadius: 4 }}>
            filter_alt
          </span>
        </div>
        {isOpen && (
          <div data-filter-popover
            style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, minWidth: 200,
                     background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)",
                     borderRadius: "var(--border-radius-md)", boxShadow: "var(--shadow-lg)",
                     padding: "var(--space-2) var(--space-3)", marginTop: 4, display: "flex",
                     flexDirection: "column", gap: "var(--space-1)" }}>
            <div className="text-xs text-muted">Dal (YYYY-MM-DD)</div>
            <input type="text" className="form-input"
              style={{ fontSize: "var(--font-size-xs)", padding: "var(--space-1) var(--space-2)", width: "100%" }}
              placeholder="2026-01-01" defaultValue={fDataDa}
              onChange={(e) => setTextFilter("data_da", e.target.value)} />
            <div className="text-xs text-muted">Al (YYYY-MM-DD)</div>
            <input type="text" className="form-input"
              style={{ fontSize: "var(--font-size-xs)", padding: "var(--space-1) var(--space-2)", width: "100%" }}
              placeholder="2026-12-31" defaultValue={fDataA}
              onChange={(e) => setTextFilter("data_a", e.target.value)} />
            {isFiltered && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 4, fontSize: "var(--font-size-xs)", width: "100%", color: "var(--indaco-danger)" }}
                onClick={() => { setFilter({ data_da: null, data_a: null }); }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13, marginRight: 4 }}>close</span>Rimuovi filtro
              </button>
            )}
          </div>
        )}
      </th>
    );
  }

  const title = "Fatture Rinascente";

  return (
    <div style={{ padding: "var(--space-4)" }}>
      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", gap: "var(--space-3)", rowGap: "var(--space-2)" }}>
        <h1 className="page-title" style={{ flexShrink: 0 }}>{title}</h1>

        {/* ── Search bar ── */}
        <div ref={searchContainerRef} style={{ position: "relative", flex: 1, maxWidth: 480, minWidth: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            border: fQ ? "1.5px solid var(--indaco-brand-primary)" : "1px solid var(--indaco-gray-300)",
            borderRadius: "var(--border-radius-md)", background: fQ ? "var(--indaco-brand-primary-light)" : "var(--indaco-white)",
            padding: "5px 12px",
            boxShadow: suggestions.length > 0 ? "var(--shadow-sm)" : "none",
            transition: "border-color .15s, background .15s, box-shadow .15s",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: fQ ? "var(--indaco-brand-primary)" : "var(--indaco-gray-400)", flexShrink: 0 }}>search</span>
            <input type="text" value={searchText}
              placeholder="Cerca: numero, fornitore, codice, file PDF, ID..."
              onChange={e => handleSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (searchText.length >= 2 && suggestions.length === 0) handleSearchInput(searchText); }}
              style={{ flex: 1, border: "none", outline: "none", background: "transparent",
                       fontSize: "var(--font-size-sm)", padding: "3px 0", minWidth: 0 }} />
            {suggestLoading && <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />}
            {(searchText || fQ) && (
              <button onClick={() => { setSearchText(""); applySearch(""); }}
                style={{ display: "flex", alignItems: "center", border: "none", background: "none", cursor: "pointer", padding: 2, flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }}>close</span>
              </button>
            )}
          </div>
          {/* Suggestions dropdown */}
          {suggestions.length > 0 && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
              background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)",
              borderRadius: "var(--border-radius-md)", boxShadow: "var(--shadow-lg)",
              overflow: "hidden", maxHeight: 360, overflowY: "auto",
            }}>
              {suggestions.map((s, i) => (
                <div key={s.id}
                  onMouseDown={() => { navigate(`/rinascente/${s.id}`); setSuggestions([]); }}
                  onMouseEnter={() => setSuggestIdx(i)}
                  style={{
                    padding: "var(--space-2) var(--space-3)", cursor: "pointer", fontSize: "var(--font-size-sm)",
                    background: i === suggestIdx ? "var(--indaco-gray-50)" : "transparent",
                    borderBottom: i < suggestions.length - 1 ? "1px solid var(--indaco-gray-100)" : "none",
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: "var(--font-weight-semibold)", fontSize: 11, color: "var(--indaco-gray-400)", fontFamily: "monospace", flexShrink: 0 }}>
                      {s.id.slice(0, 8)}
                    </span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {s.numero || s.pdf_filename || "(senza numero)"}
                    </span>
                    {s.fornitore && (
                      <code style={{ fontSize: 10, color: "var(--indaco-gray-500)", flexShrink: 0 }}>{s.fornitore.slice(0, 30)}</code>
                    )}
                  </div>
                  {(s.pdf_filename || s.codice_fornitore) && (
                    <div style={{ fontSize: 11, color: "var(--indaco-gray-400)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.pdf_filename}{s.codice_fornitore ? ` · cod ${s.codice_fornitore}` : ""}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ padding: "var(--space-2) var(--space-3)", fontSize: 11, color: "var(--indaco-gray-400)", background: "var(--indaco-gray-50)", textAlign: "center" }}>
                Invio per cercare in tutti i risultati
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexShrink: 0 }}>
          {hasAnyColumnFilter && (
            <button className="btn btn-ghost btn-sm" title="Pulisci tutti i filtri"
              onClick={() => { setFilter({ q: null, numero: null, fornitore: null, totale: null, data_da: null, data_a: null }); setSearchText(""); }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-danger)" }}>filter_list_off</span>
            </button>
          )}
          {checkMsg && <span className="text-xs" style={{ color: "var(--indaco-brand-primary)" }}>{checkMsg}</span>}
          <button className="btn btn-outline btn-sm" onClick={handleCheckNew} disabled={checking}
            title="Controlla subito se ci sono nuovi file nella cartella sorgente (forza aggiornamento)"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {checking
              ? <span className="spinner" style={{ width: 14, height: 14 }} />
              : <span className="material-symbols-outlined" style={{ fontSize: 16 }}>folder_open</span>}
            <span>Forza aggiornamento</span>
          </button>
          <span className="text-muted text-xs">{secondsAgo < 5 ? "aggiornato ora" : `${secondsAgo}s fa`}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => { load(); loadCounts(); }} title="Aggiorna">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
          </button>
        </div>
      </div>

      {/* Counters */}
      <div style={{ display: "flex", gap: "var(--space-4)", marginBottom: "var(--space-3)", fontSize: "var(--font-size-sm)", alignItems: "center", flexWrap: "wrap" }}>
        <span><span className="text-muted">Da rivedere: </span><strong style={{ color: "var(--indaco-warning)" }}>{counts?.pending ?? "—"}</strong></span>
        <span style={{ color: "var(--indaco-gray-300)" }}>|</span>
        <span><span className="text-muted">Approvati oggi: </span><strong style={{ color: "var(--indaco-success)" }}>{counts?.approvedToday ?? "—"}</strong></span>
        <span style={{ color: "var(--indaco-gray-300)" }}>|</span>
        <span><span className="text-muted">Totale: </span><strong>{counts?.total ?? "—"}</strong></span>
      </div>

      {/* Top bar: status + tipo pills */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
          {STATUS_OPTIONS.map((opt) => (
            <button key={opt.value} className={`btn btn-sm ${status === opt.value ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter({ status: opt.value === "all" ? null : opt.value })}>{opt.label}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap", alignItems: "center" }}>
          <span className="text-muted text-xs" style={{ marginRight: "var(--space-1)" }}>Tipo:</span>
          {TIPO_OPTIONS.map((opt) => (
            <button key={opt.value} className={`btn btn-sm ${tipo === opt.value ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter({ tipo: opt.value === "all" ? null : opt.value })}>{opt.label}</button>
          ))}
        </div>
      </div>

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span> Errore: {error}
        </div>
      )}

      {/* Table */}
      {data && data.items.length > 0 && (
        <div className="card" style={{ padding: 0, opacity: isLoading ? 0.6 : 1, transition: "opacity 150ms" }}>
          <div className="table-container">
            <table style={{ tableLayout: "fixed", width: "100%", minWidth: 1280 }}>
              <thead>
                <tr>
                  <ColHeader label="" sortKey="status" filterKey="status" filterType="select"
                    filterValue={status} options={STATUS_OPTIONS} style={{ width: 40, textAlign: "center" }} />
                  <ColHeader label="N. documento" sortKey="numero" filterKey="numero" filterType="text"
                    filterValue={fNumero} style={{ width: 130 }} />
                  <ColHeader label="Fornitore" sortKey="fornitore" filterKey="fornitore" filterType="text"
                    filterValue={fFornitore} style={{ width: 230 }} />
                  <ColHeader label="Tipo" sortKey="status" filterKey="tipo" filterType="select"
                    filterValue={tipo} options={TIPO_OPTIONS} style={{ width: 110 }} />
                  <DateRangeHeader style={{ width: 100 }} />
                  <th style={{ width: 60, textAlign: "center", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-600)" }}>Valuta</th>
                  <ColHeader label="Totale" sortKey="totale" filterKey="totale" filterType="text"
                    filterValue={fTotale} style={{ width: 110, textAlign: "right" }} />
                  <th style={{ width: 70, textAlign: "center", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-600)" }}>Compl.</th>
                  <th style={{ width: 44, textAlign: "center", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-600)" }}>Allg.</th>
                  <ColHeader label="Ricezione" sortKey="date" style={{ width: 95 }} />
                  <th style={{ width: 40, textAlign: "center", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-600)" }}></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => {
                  const totaleNum = p.totale != null ? Number(p.totale) : null;
                  const isNegative = totaleNum != null && totaleNum < 0;
                  const completeness = p.completeness ?? 0;
                  const showCompleteness = p.status === "pending_review" || p.status === "approved";
                  return (
                    <tr key={p.id}
                      style={p.id === lastVisitedId ? { background: "var(--indaco-brand-primary-light)" } : undefined}
                      onClick={() => { setLastVisitedId(p.id); localStorage.setItem("lastVisitedRinascenteId", p.id); navigate(`/rinascente/${p.id}`); }}>
                      {/* Stato */}
                      <td style={{ textAlign: "center", overflow: "hidden" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                          {getStatusIcon(p.status)}
                          {/* Chi tiene la pratica in carico (2026-07-28) — mancava
                              del tutto su Rinascente: ora e' visibile come sugli altri flow. */}
                          <LockChip lockedBy={p.locked_by} lockedByName={p.locked_by_name} lockedAt={p.locked_at} />
                        </span>
                      </td>
                      {/* Numero doc */}
                      <td style={{ overflow: "hidden" }} title={p.numero ?? p.source_ref ?? ""}>
                        <code className="truncate" style={{ display: "block", fontFamily: "monospace", fontSize: "var(--font-size-sm)" }}>
                          {p.numero || p.source_ref || "—"}
                        </code>
                      </td>
                      {/* Fornitore */}
                      <td style={{ overflow: "hidden" }} title={p.fornitore ?? p.ragsoc_filename ?? ""}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                          <span className="truncate" style={{ flex: 1, fontSize: "var(--font-size-sm)" }}>
                            {p.fornitore || p.ragsoc_filename || "—"}
                          </span>
                          {p.codice_fornitore_filename && (
                            <code style={{ fontSize: 10, color: "var(--indaco-gray-500)", flexShrink: 0 }} title="Codice fornitore Oracle (dal filename)">
                              {p.codice_fornitore_filename}
                            </code>
                          )}
                        </div>
                      </td>
                      {/* Tipo */}
                      <td style={{ overflow: "hidden" }}>
                        {p.is_credit_memo ? (
                          <span className="badge badge-warning" title="Credit memo / nota di credito">Credit memo</span>
                        ) : p.tipo_doc === "fattura" ? (
                          <span className="badge badge-info" title="Fattura">Fattura</span>
                        ) : (
                          <span className="badge badge-neutral" title="Tipo non determinato">—</span>
                        )}
                      </td>
                      {/* Data emissione */}
                      <td style={{ overflow: "hidden" }} className="text-sm">
                        {fmtDateOnly(p.data_emissione)}
                      </td>
                      {/* Valuta */}
                      <td style={{ textAlign: "center", fontSize: "var(--font-size-sm)" }}>
                        {p.valuta || "—"}
                      </td>
                      {/* Totale */}
                      <td style={{ textAlign: "right", fontSize: "var(--font-size-sm)", whiteSpace: "nowrap", color: isNegative ? "var(--indaco-danger)" : "inherit", fontWeight: isNegative ? "var(--font-weight-semibold)" : "inherit" }}
                          title={totaleNum != null ? String(totaleNum) : undefined}>
                        {totaleNum != null
                          ? totaleNum.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : "—"}
                      </td>
                      {/* Completeness */}
                      <td style={{ textAlign: "center" }}>
                        {showCompleteness ? (
                          <span style={{ fontWeight: "var(--font-weight-bold)", fontSize: "var(--font-size-sm)", color: completenessColor(completeness) }}>
                            {Math.round(completeness * 100)}%
                          </span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      {/* Allg. (email) */}
                      <td style={{ textAlign: "center" }}>
                        {p.email_message_id ? (
                          <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-info)" }} title="Email originale disponibile">mail</span>
                        ) : (
                          <span className="text-muted text-xs">—</span>
                        )}
                      </td>
                      {/* Ricezione */}
                      <td style={{ whiteSpace: "nowrap", overflow: "hidden" }} className="text-sm">
                        <span title={formatDateFull(p.created_at)}>{formatDate(p.created_at)}</span>
                      </td>
                      {/* Azioni (freccia di navigazione) */}
                      <td style={{ textAlign: "center" }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }} title="Apri dettaglio">open_in_new</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.items.length === 0 && !isLoading && (
        <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>Nessuna fattura trovata</div>
      )}

      {/* Pagination */}
      {data && data.total > 0 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "var(--space-3)", gap: "var(--space-2)" }}>
          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setFilter({ page: page === 2 ? null : String(page - 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_left</span>
              </button>
              {(() => {
                const pages: (number | "...")[] = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  pages.push(1);
                  if (page > 3) pages.push("...");
                  const start = Math.max(2, page - 1);
                  const end = Math.min(totalPages - 1, page + 1);
                  for (let i = start; i <= end; i++) pages.push(i);
                  if (page < totalPages - 2) pages.push("...");
                  pages.push(totalPages);
                }
                return pages.map((p, i) =>
                  p === "..." ? <span key={`e${i}`} className="text-muted" style={{ padding: "0 4px" }}>…</span>
                    : <button key={p} className={page === p ? "active" : ""}
                        onClick={() => setFilter({ page: p === 1 ? null : String(p) })}>{p}</button>
                );
              })()}
              <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_right</span>
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>
            <span className="text-muted">Mostra</span>
            <select className="form-input" value={pageSize}
              style={{ width: 70, padding: "2px 6px", fontSize: "var(--font-size-sm)" }}
              onChange={(e) => setFilter({ size: e.target.value === String(DEFAULT_PAGE_SIZE) ? null : e.target.value, page: null })}>
              {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-muted">di {data.total}</span>
          </div>
        </div>
      )}
    </div>
  );
}
